import React, { useState, useRef, useEffect } from "react";
import {
  ArrowLeft,
  Trash2,
  Clock,
  User,
  Award,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  Edit2,
} from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../App";

export const SessionHeader = ({
  sessionData,
  onBack,
  onStatusChange,
  onDelete,
  onEdit,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getStatusConfig = (status) => {
    switch (status) {
      case "completed":
        return {
          gradient: "from-emerald-500/20 to-emerald-600/20",
          border: "border-emerald-500/30",
          text: "text-emerald-500",
          bg: "bg-emerald-500",
          icon: CheckCircle,
          label: t("sessionDetails.status.completed"),
        };
      case "cancelled":
        return {
          gradient: "from-rose-500/20 to-rose-600/20",
          border: "border-rose-500/30",
          text: "text-rose-500",
          bg: "bg-rose-500",
          icon: XCircle,
          label: t("sessionDetails.status.cancelled"),
        };
      case "no-show":
        return {
          gradient: "from-orange-500/20 to-orange-600/20",
          border: "border-orange-500/30",
          text: "text-orange-500",
          bg: "bg-orange-500",
          icon: AlertCircle,
          label: t("sessionDetails.status.noShow"),
        };
      case "scheduled":
      default:
        return {
          gradient: "from-blue-500/20 to-indigo-600/20",
          border: "border-blue-500/30",
          text: "text-blue-500",
          bg: "bg-blue-500",
          icon: Clock,
          label: t("sessionDetails.status.scheduled"),
        };
    }
  };

  const statusConfig = getStatusConfig(sessionData.status);
  const StatusIcon = statusConfig.icon;

  const statusOptions = [
    {
      value: "scheduled",
      label: t("sessionDetails.status.scheduled"),
      icon: Clock,
      colorClass: "text-blue-500",
    },
    {
      value: "completed",
      label: t("sessionDetails.status.completed"),
      icon: CheckCircle,
      colorClass: "text-emerald-500",
    },
    {
      value: "cancelled",
      label: t("sessionDetails.status.cancelled"),
      icon: XCircle,
      colorClass: "text-rose-500",
    },
    {
      value: "no-show",
      label: t("sessionDetails.status.noShow"),
      icon: AlertCircle,
      colorClass: "text-orange-500",
    },
  ];

  const handleStatusSelect = (status) => {
    onStatusChange(status);
    setShowStatusDropdown(false);
  };

  const isStatusScheduled = (sessionData.status || "scheduled") === "scheduled";
  const isDisabledForTrainer = user?.role === 'trainer' && !isStatusScheduled;

  return (
    <>
      {/* Integrated Navigation & Info Bar */}
      <div
        className={`
        sticky top-0 z-[60] backdrop-blur-xl bg-white/90 dark:bg-black/80
        border-b ${colors.tableBorder} px-4 md:px-6 py-3 transition-all duration-300 shadow-sm
      `}
      >
        <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3 sm:gap-4 max-w-[1400px] mx-auto">
          {/* Left: Back & Title */}
          <div className="flex items-center space-x-3 shrink-0 order-1">
            <button
              onClick={onBack}
              className={`
                group p-2 rounded-xl transition-all duration-300
                ${colors.bgHover} hover:scale-105 active:scale-95
                border border-transparent hover:border-gray-200 dark:hover:border-white/10
              `}
            >
              <ArrowLeft
                className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.textSecondary} group-hover:text-primary transition-colors`}
              />
            </button>
            <div className="flex flex-col">
              <h1
                className={`text-lg sm:text-xl font-bold ${colors.textPrimary} tracking-tight leading-tight`}
              >
                {t("sessionDetails.pageTitle")}
              </h1>
              <span
                className={`text-[10px] sm:text-xs ${colors.textTertiary} font-medium leading-none mt-0.5`}
              >
                ID: #{sessionData.id?.slice(-6).toUpperCase()}
              </span>
            </div>
          </div>

          {/* Middle: Compact Session Info (Date, Time, Trainer, Package) */}
          <div className="flex w-full md:w-auto basis-full md:basis-auto md:flex-1 items-center gap-5 md:gap-6 px-1 md:px-4 md:border-x border-t md:border-t-0 border-gray-200 dark:border-white/10 pt-3 md:pt-0 mt-1 md:mt-0 mx-0 md:mx-2 overflow-x-auto whitespace-nowrap hide-scrollbar order-3 md:order-2">
            {/* Date & Time */}
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-center justify-center">
                <span
                  className={`text-[9px] font-bold uppercase ${colors.textTertiary} leading-none mb-[1px]`}
                >
                  {moment(sessionData.startTime).format("MMM")}
                </span>
                <span
                  className={`text-lg font-black ${colors.textPrimary} leading-none`}
                >
                  {moment(sessionData.startTime).format("D")}
                </span>
              </div>
              <div className="flex flex-col justify-center">
                <div
                  className={`text-sm font-bold ${colors.textPrimary} tracking-tight leading-none`}
                >
                  {moment(sessionData.startTime).format("HH:mm")} -{" "}
                  {moment(sessionData.endTime).format("HH:mm")}
                </div>
                <span
                  className={`text-[10px] ${colors.textSecondary} leading-tight mt-0.5`}
                >
                  {moment(sessionData.startTime).format("dddd")}
                </span>
              </div>
            </div>

            {/* Trainer Info */}
            <div className="flex flex-col justify-center">
              <span
                className={`text-[9px] font-bold uppercase tracking-wider ${colors.textTertiary} leading-none mb-1`}
              >
                {t("sessionDetails.trainer")}
              </span>
              <span
                className={`text-sm font-bold ${colors.textPrimary} leading-none`}
              >
                {sessionData.trainerName}
              </span>
            </div>

            {/* Package Info */}
            <div className="flex flex-col justify-center">
              <span
                className={`text-[9px] font-bold uppercase tracking-wider ${colors.textTertiary} leading-none mb-1`}
              >
                {t("sessionDetails.package")}
              </span>
              <span
                className={`text-sm font-bold ${colors.textPrimary} leading-none truncate max-w-[150px]`}
              >
                {sessionData.packageName || t("sessionDetails.defaultPackage")}
              </span>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 order-2 md:order-3">
            {/* Edit Button */}
            {onEdit && (
              <button
                onClick={(e) => {
                  if (isDisabledForTrainer) e.preventDefault();
                  else onEdit(e);
                }}
                disabled={isDisabledForTrainer}
                className={`p-2 ${colors.bgHover} rounded-xl transition-all duration-200 text-blue-500 border border-transparent ${isDisabledForTrainer ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-500/10 hover:shadow-sm hover:border-blue-500/20'}`}
                title={t("sessionDetails.editSession", "Oturumu Düzenle")}
              >
                <Edit2 className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            )}

            {/* Delete Button */}
            <button
              onClick={(e) => {
                if (isDisabledForTrainer) e.preventDefault();
                else onDelete(e);
              }}
              disabled={isDisabledForTrainer}
              className={`p-2 ${colors.bgHover} rounded-xl transition-all duration-200 text-rose-500 border border-transparent ${isDisabledForTrainer ? 'opacity-50 cursor-not-allowed' : 'hover:bg-rose-500/10 hover:shadow-sm hover:border-rose-500/20'}`}
              title={t("sessionDetails.deleteSession")}
            >
              <Trash2 className="w-4 h-4 sm:w-4 sm:h-4" />
            </button>

            {/* Status Dropdown */}
            <div
              className="relative min-w-[130px] sm:min-w-[140px]"
              ref={dropdownRef}
            >
              <button
                disabled={isDisabledForTrainer}
                onClick={() => {
                  if (!isDisabledForTrainer) setShowStatusDropdown(!showStatusDropdown);
                }}
                className={`
                    w-full h-9 sm:h-10 flex items-center justify-between px-3 rounded-xl transition-all duration-300
                    bg-gray-50 dark:bg-white/5 shadow-sm border border-transparent outline-none
                    ${isDisabledForTrainer ? 'opacity-60 cursor-not-allowed' : 'hover:border-gray-200 dark:hover:border-white/10'}
                    ${showStatusDropdown ? "ring-2 ring-blue-500/50" : ""}
                 `}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`flex items-center justify-center ${statusConfig.text}`}
                  >
                    <StatusIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                  <span
                    className={`text-xs font-bold ${colors.textPrimary} leading-none truncate`}
                  >
                    {statusConfig.label}
                  </span>
                </div>
                <ChevronDown
                  className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${colors.textTertiary} ml-1 transition-transform duration-300 ${showStatusDropdown ? "rotate-180" : ""}`}
                />
              </button>

              {showStatusDropdown && (
                <div
                  className={`absolute top-full right-0 mt-2 w-48 p-1.5 rounded-xl z-[100] bg-white dark:bg-zinc-800 border ${colors.tableBorder} shadow-2xl overflow-hidden`}
                >
                  <div className="flex flex-col gap-1">
                    {statusOptions.map((option) => {
                      const isActive = sessionData.status === option.value;
                      const OptionIcon = option.icon;
                      return (
                        <button
                          key={option.value}
                          onClick={() => handleStatusSelect(option.value)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all duration-200 ${isActive ? "bg-gray-100 dark:bg-white/10" : "hover:bg-gray-50 dark:hover:bg-white/5"}`}
                        >
                          <OptionIcon
                            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${option.colorClass}`}
                          />
                          <span
                            className={`text-xs font-medium ${isActive ? colors.textPrimary : colors.textSecondary}`}
                          >
                            {option.label}
                          </span>
                          {isActive && (
                            <CheckCircle
                              className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ml-auto ${option.colorClass}`}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SessionHeader;
